import { AxiosError } from "axios";
import clsx from "clsx";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import {
  groupAccessIdFromGroupId,
  studentAccessIdFromUserId,
} from "../../../../../../collaboration/src/shared/access-id";
import { useAssessmentActions } from "../../../../utils/assessment";

import { AssessWithAIModeButton } from "./AssessWithAIModeButton";
import { AssessmentPoints } from "./AssessmentPoints";
import { FeedbackStyleRecordingSwitch } from "./FeedbackStyleRecordingSwitch";
import { OfficialSubmissionFooter } from "./OfficialSubmissionFooter";
import { OutcomeAssessment } from "./OutcomeAssessment";

import { getOrRunAiAssessment } from "components/ai/queries/assessment-cache";
import { AIAssessmentResponseType } from "components/ai/queries/assessment-query";
import { ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import {
  calculatePointsFromOutcomeScores,
  isNoOutcomeScored,
} from "components/materials/page/helpers";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { ContentSidebarMain } from "components/materials/sidebar/ContentSidebarMain";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { showConfirmationModal } from "mds/components/Modal";
import { BaseTextArea } from "mds/components/TextArea";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { DoubleSparkleIcon, LoadingDotsIcon } from "mds/icons";
import {
  SaveSource,
  useAssessmentGradingFormContext,
} from "providers/AssessmentGradingFormProvider";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  sectionUserSelectors,
  selectAssessmentBySubmissionId,
  selectCurrentOrgRubric,
  selectCurrentSectionUserId,
  selectFullSubmissionById,
  selectIsAssessWithAIModeEnabled,
  selectIsRecordingFeedbackStyle,
  selectNestedPageOutcomes,
  selectOutcomeAssessmentByAssessmentId,
  selectSortedUserFullSubmissionsByPageId,
  selectUserOfficialSubmissionByPageId,
} from "store/selectors";
import { isIndividualSubmission } from "store/selectors/type-helpers";
import { viewStateActions } from "store/slices/view";
import { toastErrorMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { maybeRecordFeedbackStyleSample } from "utils/assessment-feedback-style-recording";
import { useQueryParamsManager } from "utils/routing";
import { isAssessmentAIFeedbackStyleEnabled } from "utils/waffle-flags";

interface AssessmentGradingProps {
  page: PageType;
  nextId: string | null;
  prevId: string | null;
}

type AssessmentFormInputType = {
  points: number | null | undefined;
  comment: string;
  page_outcomes: Record<string, number>;
  is_official: boolean;
};

type AssessmentFormErrors = AxiosError & {
  response: {
    data: {
      non_field_errors: string[];
    };
  };
};

const buildFilteredParams = (params: AssessmentFormInputType, is_official: boolean) => {
  const filteredPageOutcomes = Object.entries(params.page_outcomes).filter(
    ([, score]) => !isNaN(score),
  );
  return {
    ...params,
    is_official,
    page_outcomes: Object.fromEntries(filteredPageOutcomes),
  };
};

export const AssessmentGradingForm: FC<AssessmentGradingProps> = ({ page, nextId, prevId }) => {
  const { setIsAiAssessmentLoading, showSaveConfirmation } = useAssessmentGradingFormContext();
  const { getQueryParam, setQueryParams } = useQueryParamsManager();

  const dispatch = useAppDispatch();
  const orgRubric = useAppSelector(selectCurrentOrgRubric);
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const pageOutcomes = useAppSelector((s) => selectNestedPageOutcomes(s, page.id));
  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));
  const assessment = useAppSelector((s) => selectAssessmentBySubmissionId(s, submissionId)) || null;
  const outcomeAssessments = useAppSelector((s) =>
    selectOutcomeAssessmentByAssessmentId(s, assessment?.id),
  );

  const sectionUserMap = useAppSelector(sectionUserSelectors.selectEntities);
  const maxPoints = page.max_points;
  const hasOutcomes = pageOutcomes.length > 0;
  const isAssessing = useIsAssessing();

  let selectedAccessId: string | undefined;

  if (submission) {
    if (isIndividualSubmission(submission)) {
      const userId = sectionUserMap[submission.section_user_id]?.user_id;
      if (userId) {
        selectedAccessId = studentAccessIdFromUserId(userId);
      }
    } else if (submission.page_group_id) {
      selectedAccessId = groupAccessIdFromGroupId(submission.page_group_id);
    }
  }

  const { createAssessment, updateAssessment, deleteAssessment } = useAssessmentActions();

  const officialSubmission = useAppSelector((s) =>
    selectUserOfficialSubmissionByPageId(s, page.id, selectedAccessId),
  );
  const submissionsForUser = useAppSelector((s) =>
    selectSortedUserFullSubmissionsByPageId(s, page.id, selectedAccessId),
  );

  const isOfficial = !!submission?.official_at;

  const blankFormValues = useMemo(
    () => ({
      points: NaN,
      comment: "",
      page_outcomes: Object.fromEntries(pageOutcomes.map((po) => [po.id, NaN])),
      is_official: false,
    }),
    [pageOutcomes],
  );

  const assessmentFormValues = useMemo(() => {
    return {
      points: assessment?.points,
      comment: assessment?.comment,
      page_outcomes: Object.fromEntries(
        outcomeAssessments.map((oa) => [oa.page_outcome_id, oa.score]),
      ),
      is_official: !!submission?.official_at,
    };
  }, [assessment, outcomeAssessments, submission?.official_at]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<AssessmentFormInputType>({
    defaultValues: assessment ? assessmentFormValues : blankFormValues,
  });

  const scores = Object.values(watch("page_outcomes"));
  const points = watch("points");
  const comment = watch("comment");

  const isManuallyScoring = points
    ? calculatePointsFromOutcomeScores(scores, maxPoints, orgRubric) !== points
    : false;

  const hasPoints = maxPoints !== 0;
  const isAnyOutcomeScored = scores !== undefined && scores.some((os) => !isNaN(os));

  // The points configuration is only shown _after_ the user has selected an outcome score for any outcome.
  // TODO: It'd be nice to change this design in the future so that the config is only hidden when not
  //  applicable, not when outcome scores aren't selected.
  const showPoints = hasPoints && (isManuallyScoring || isAnyOutcomeScored);

  const formIsBlank = comment === "" && scores.every(isNaN) && !points;
  const showDelete = assessment?.id && formIsBlank;
  const hasMultipleSubmissions = submissionsForUser.length > 1;
  const showOfficialFooterActionable =
    hasMultipleSubmissions && officialSubmission && !isOfficial && !formIsBlank;
  const showOfficialFooterReadOnly = hasMultipleSubmissions && isOfficial;
  const showOfficialFooter = showOfficialFooterActionable || showOfficialFooterReadOnly;
  const isFormDirty =
    showDelete ||
    (!formIsBlank && !assessment) ||
    (assessment && (comment !== assessment.comment || points !== assessment.points)) ||
    scores.some((score, index) => {
      const pageOutcomeId = pageOutcomes[index]?.id;
      const outcomeAssessment = outcomeAssessments.find(
        (oa) => oa.page_outcome_id === pageOutcomeId,
      );
      return outcomeAssessment ? score !== outcomeAssessment.score : !isNaN(score);
    });

  const setScore = useCallback(
    (pageOutcomeId: string, score: number) => {
      setValue(`page_outcomes.${pageOutcomeId}`, score);
    },
    [setValue],
  );

  const updatePoints = useCallback(
    (newPoints?: number | null) => {
      const updated_scores = Object.values(watch("page_outcomes"));
      if (newPoints !== undefined && (newPoints === null || !isNaN(newPoints))) {
        setValue("points", newPoints);
      } else if (!isManuallyScoring || isNoOutcomeScored(updated_scores)) {
        setValue("points", calculatePointsFromOutcomeScores(updated_scores, maxPoints, orgRubric));
      }
    },
    [setValue, isManuallyScoring, maxPoints, orgRubric, watch],
  );

  const setScoreAndUpdatePoints = (pageOutcomeId: string, score: number) => {
    setScore(pageOutcomeId, score);
    updatePoints();
  };

  /// ////////// AI related code ///////////////

  // Having the button toggled is equivalent to clicking the AI assessment button,
  // every time the form is empty.
  const isAssessWithAIModeEnabled = useAppSelector(selectIsAssessWithAIModeEnabled);
  const [isAiAssessment, setIsAiAssessment] = useState(false);
  const [isAiAssessmentPromiseResolved, setIsAiAssessmentPromiseResolved] = useState(false);
  const [hasResetAiAssessment, setHasResetAiAssessment] = useState(false);
  // We track the request for outcome assessments rather than outcomeAssessments.length
  // because a comment-only assessment on a page with outcomes legitimately has zero outcome assessments.
  const [isLoadingOutcomeAssessments, setIsLoadingOutcomeAssessments] = useState(false);

  const isDoingAiAssessment = isAiAssessment && isAssessing;
  const isLoading = isDoingAiAssessment && !isAiAssessmentPromiseResolved;
  useEffect(() => {
    setIsAiAssessmentLoading(isLoading);
    return () => {
      setIsAiAssessmentLoading(false);
    };
  }, [isLoading, setIsAiAssessmentLoading]);
  const finishedLoading = isDoingAiAssessment && isAiAssessmentPromiseResolved;
  const isRecordingAssessmentStyle =
    useAppSelector(selectIsRecordingFeedbackStyle) && isAssessmentAIFeedbackStyleEnabled();
  const isDisabled = isLoading;

  const onClickResetAiAssessment = () => {
    setIsAiAssessment(false);
    reset(assessment ? assessmentFormValues : blankFormValues);
    if (submission?.id) {
      storeApi.assessments.list({ submission_id: submission.id });
    }
    setHasResetAiAssessment(true);
  };

  const setFormToAiAssessment = useCallback(
    (aiAssessment: AIAssessmentResponseType) => {
      aiAssessment.outcomes.forEach((oa) => {
        setScore(oa.pageOutcomeId, oa.score);
      });

      const outcomesScores = aiAssessment.outcomes.map((oa) => oa.score);
      const newPoints = calculatePointsFromOutcomeScores(outcomesScores, maxPoints, orgRubric);
      updatePoints(newPoints);
      setValue("comment", aiAssessment.feedback);
      if (aiAssessment.errorMessage) {
        setError("root", {
          type: "manual",
          message: aiAssessment.errorMessage,
        });
      } else {
        clearErrors("root");
      }
    },
    [setScore, setValue, maxPoints, orgRubric, setError, clearErrors, updatePoints],
  );

  const onClickAiAssess = useCallback(
    async (userClicked = false) => {
      if (!submission) return;
      setIsAiAssessment(true);
      setHasResetAiAssessment(false);
      setIsAiAssessmentPromiseResolved(false);
      // TODO: Show an option to re-generate the AI assessment (in case of error or simply for a refresh)
      const aiAssessment = await getOrRunAiAssessment(submission, false);
      trackEvent("AI assessment generated", {
        ...GENERAL_EVENT_CATEGORY,
        userClicked,
        submissionId: submission.id,
        pageId: submission.page_id,
      });
      setFormToAiAssessment(aiAssessment);
      setIsAiAssessmentPromiseResolved(true);
    },
    [submission, setFormToAiAssessment],
  );

  const maybeAssessWithAI = useCallback(() => {
    const shouldAssess =
      isAssessWithAIModeEnabled &&
      submission &&
      !isFormDirty &&
      !assessment &&
      !hasResetAiAssessment &&
      !isAiAssessment;
    if (shouldAssess) {
      onClickAiAssess(false);
    }
  }, [
    isAssessWithAIModeEnabled,
    submission,
    assessment,
    isFormDirty,
    hasResetAiAssessment,
    isAiAssessment,
    onClickAiAssess,
  ]);

  useEffect(() => {
    if (submission?.id) {
      setIsAiAssessment(false);
      setIsAiAssessmentPromiseResolved(false);
      setHasResetAiAssessment(false);
      clearErrors("root");
    }
  }, [submission?.id, clearErrors]);

  useEffect(() => {
    maybeAssessWithAI();
    // Handle changes to these dependencies separately than those below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssessWithAIModeEnabled, isFormDirty, hasResetAiAssessment, isAiAssessment]);

  useEffect(() => {
    if (submission?.id && !assessment) {
      if (isFormDirty && !isLoading) {
        reset(blankFormValues);
      }
      maybeAssessWithAI();
      storeApi.assessments.list({ submission_id: submission.id });
    }
    // Handle changes to these dependencies separately than those above, which
    // ensures the form is always reset before the AI assessment content is loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission?.id, assessment?.id]);

  /// ////////// End of AI related code ///////////////

  useEffect(() => {
    if (!assessment?.id) {
      setIsLoadingOutcomeAssessments(false);
      return;
    }

    setIsLoadingOutcomeAssessments(true);
    storeApi.outcome_assessments
      .list({ assessment_id: assessment.id })
      .finally(() => setIsLoadingOutcomeAssessments(false));
  }, [assessment?.id]);

  useEffect(() => {
    if (!assessment?.id) return;

    const page_outcomes = Object.fromEntries(
      pageOutcomes.map((po) => {
        const outcomeAssessment = outcomeAssessments.find((oa) => oa.page_outcome_id === po.id);
        return [po.id, outcomeAssessment ? outcomeAssessment.score : NaN];
      }),
    );
    reset({ comment: assessment.comment || "", points: assessment.points, page_outcomes });
  }, [assessment, outcomeAssessments, pageOutcomes, reset]);

  useEffect(() => {
    dispatch(viewStateActions.setShouldShowModalOnExitAssessmentMode(!formIsBlank && isFormDirty));
    return () => {
      dispatch(viewStateActions.setShouldShowModalOnExitAssessmentMode(false));
    };
  }, [isFormDirty, dispatch, formIsBlank]);

  useEffect(() => {
    dispatch(viewStateActions.setShouldShowModalOnExitAssessmentMode(false));
  }, [dispatch, assessment?.id]);

  const onSubmit: SubmitHandler<AssessmentFormInputType> = async (params, e) => {
    if (!e || !submission) return;
    if (!sectionUserId) {
      toastErrorMessage(t("assessment_card.cannot_assess_without_enrollment"));
      return;
    }

    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
    const submitSource = submitter?.value;

    try {
      const assessmentId = assessment?.id;
      const shouldMakeOfficial = !officialSubmission || isOfficial;
      const filteredParams = buildFilteredParams(params, shouldMakeOfficial);

      let didPersist = false;
      if (isFormDirty) {
        if (assessmentId) {
          if (
            !filteredParams.comment &&
            !filteredParams.points &&
            Object.values(filteredParams.page_outcomes).length === 0
          ) {
            const confirmed = await showConfirmationModal({
              title: t("assessment.delete_modal.title"),
              labelText: t("assessment.delete_modal.description"),
              confirmButtonText: t("common.delete"),
              confirmButtonKind: "caution",
              cancelButtonText: t("assessment.delete_modal.keep_assessment"),
            });
            if (confirmed) {
              trackEvent("Assessment - Delete", {
                ...GENERAL_EVENT_CATEGORY,
                submitSource,
                assessmentId,
                submissionId: submission.id,
                pageId: page.id,
              });
              await deleteAssessment(assessmentId);
            }
          } else {
            trackEvent("Assessment - Update", {
              ...GENERAL_EVENT_CATEGORY,
              submitSource,
              assessmentId,
              submissionId: submission.id,
              pageId: page.id,
            });
            await updateAssessment(assessmentId, filteredParams, sectionUserId, submission.id);
            maybeRecordFeedbackStyleSample(submission.id, filteredParams.comment ?? "");
            didPersist = true;
          }
        } else {
          trackEvent("Assessment - Create", {
            ...GENERAL_EVENT_CATEGORY,
            submitSource,
            submissionId: submission.id,
            pageId: page.id,
          });
          await createAssessment(filteredParams, sectionUserId, submission.id, page.section_id);
          maybeRecordFeedbackStyleSample(submission.id, filteredParams.comment ?? "");
          didPersist = true;
        }
      }

      if (didPersist && selectedAccessId && submitSource != null) {
        const source: SaveSource =
          submitSource === "next" || submitSource === "prev" ? submitSource : "queue";
        showSaveConfirmation({ source, accessId: selectedAccessId });
      }

      if (submitSource === "next" && nextId) {
        setQueryParams({
          [ACCESS_ID_QUERY_PARAM]: nextId,
          [SUBMISSION_ID_QUERY_PARAM]: null,
        });
      } else if (submitSource === "prev" && prevId) {
        setQueryParams({
          [ACCESS_ID_QUERY_PARAM]: prevId,
          [SUBMISSION_ID_QUERY_PARAM]: null,
        });
      } else if (submitSource?.startsWith("queue")) {
        const responseId = submitSource.split(":")[1];
        setQueryParams(
          {
            [ACCESS_ID_QUERY_PARAM]: responseId,
            [SUBMISSION_ID_QUERY_PARAM]: null,
          },
          { replace: false },
        );
      }
    } catch (error) {
      const axiosError = error as AssessmentFormErrors;
      // TODO: better error handling
      const responseData = axiosError.response?.data;
      const nonFieldErrors = responseData?.non_field_errors;
      setError("root", {
        type: "manual",
        message:
          (Array.isArray(nonFieldErrors) && nonFieldErrors[0]) ||
          (Array.isArray(responseData) && responseData[0]) ||
          "",
      });
    }
  };

  const onClickSetAsOfficial = useCallback(async () => {
    if (!submission || !sectionUserId) {
      if (!sectionUserId) {
        toastErrorMessage(t("assessment_card.cannot_assess_without_enrollment"));
      }
      return;
    }

    const params = watch();
    const filteredParams = buildFilteredParams(params, true);

    try {
      const assessmentId = assessment?.id;
      if (assessmentId) {
        trackEvent("Assessment - Set as official", {
          ...GENERAL_EVENT_CATEGORY,
          assessmentId,
          submissionId: submission.id,
          pageId: page.id,
        });
        await updateAssessment(assessmentId, filteredParams, sectionUserId, submission.id);
        maybeRecordFeedbackStyleSample(submission.id, filteredParams.comment ?? "");
      } else if (!formIsBlank) {
        trackEvent("Assessment - Set as official", {
          ...GENERAL_EVENT_CATEGORY,
          submissionId: submission.id,
          pageId: page.id,
        });
        await createAssessment(filteredParams, sectionUserId, submission.id, page.section_id);
        maybeRecordFeedbackStyleSample(submission.id, filteredParams.comment ?? "");
      }

      if (selectedAccessId) {
        showSaveConfirmation({ source: "setOfficial", accessId: selectedAccessId });
      }
    } catch (error) {
      const axiosError = error as AssessmentFormErrors;
      const responseData = axiosError.response?.data;
      const nonFieldErrors = responseData?.non_field_errors;
      setError("root", {
        type: "manual",
        message:
          (Array.isArray(nonFieldErrors) && nonFieldErrors[0]) ||
          (Array.isArray(responseData) && responseData[0]) ||
          "",
      });
    }
  }, [
    assessment?.id,
    createAssessment,
    formIsBlank,
    page.id,
    page.section_id,
    sectionUserId,
    selectedAccessId,
    setError,
    showSaveConfirmation,
    submission,
    updateAssessment,
    watch,
  ]);

  const handleFormFocus = () => {
    clearErrors("root");
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ContentSidebarMain bottomPadding={false} className="min-h-0 flex-1">
        <div className="mt-2 flex flex-col gap-4 pb-3">
          <div className="flex flex-col gap-1">
            <AssessWithAIModeButton />
            <FeedbackStyleRecordingSwitch />
          </div>

          <form
            className="relative data-[loading]:animate-pulse"
            data-loading={isLoading || undefined}
            id="assessment-grading-form"
            onFocus={handleFormFocus}
            onSubmit={handleSubmit(onSubmit)}
          >
            {isAiAssessment && (
              <div className="body-xs mb-2 flex items-center gap-2 rounded-md bg-lavender-tint-95 px-2 py-1 text-black-tint-20">
                <DoubleSparkleIcon />
                {t("assessment_card.ai.ai_generated")}
              </div>
            )}

            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
                <LoadingDotsIcon />
              </div>
            )}

            <div
              className={clsx("animate-shine-base relative", {
                "animate-shine-active": finishedLoading,
              })}
            >
              {hasOutcomes && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    {pageOutcomes.map((pageOutcome) => (
                      <OutcomeAssessment
                        disabled={isDisabled}
                        key={pageOutcome.id}
                        pageOutcome={pageOutcome}
                        register={register(`page_outcomes.${pageOutcome.id}`, {
                          valueAsNumber: true,
                          required: false,
                        })}
                        score={watch(`page_outcomes.${pageOutcome.id}`)}
                        setScore={setScoreAndUpdatePoints}
                      />
                    ))}
                  </div>

                  {showPoints && (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <h3 className="mx-0 my-0 text-sm font-semibold text-black-tint-20">
                          {t("submission_assessment.total_points")}
                        </h3>

                        <AssessmentPoints
                          disabled={isDisabled}
                          page={page}
                          points={watch("points")}
                          onSave={(currentPoints) => {
                            updatePoints(currentPoints);
                          }}
                        />
                      </div>

                      <div className="flex w-full justify-end">
                        {isManuallyScoring && (
                          <SquareButton
                            kind="ghost"
                            size="xs"
                            onClick={() => {
                              updatePoints(
                                calculatePointsFromOutcomeScores(scores, maxPoints, orgRubric),
                              );
                            }}
                          >
                            {t("common.reset_points")}
                          </SquareButton>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 flex w-full flex-col gap-1">
                <h4 className="mx-0 my-0 text-sm font-semibold">
                  {t("submission_assessment.instructor_comment")}
                </h4>

                <BaseTextArea
                  {...register("comment", {
                    disabled: isDisabled,
                  })}
                  aria-label="Comment"
                  className="mb-1 text-black-tint-20"
                  minRows={3}
                  placeholder={t("submission_assessment.comment_placeholder")}
                  size="s"
                  onClick={() => {
                    trackEvent("Assessment - Add comment", {
                      ...BUTTON_EVENT_CATEGORY,
                      pageId: page.id,
                      submissionId: submission?.id,
                    });
                  }}
                />
              </div>
            </div>

            {errors.root && <span className="body-s text-red">{errors.root.message}</span>}

            <div className="mt-2 flex flex-row justify-end gap-1">
              {isAiAssessment ? (
                <SquareButton
                  disabled={isDisabled || isRecordingAssessmentStyle}
                  eventName="Assessment - Reset"
                  eventProps={{ pageId: page.id, submissionId: submission?.id }}
                  kind="tertiary"
                  size="xs"
                  onClick={onClickResetAiAssessment}
                >
                  {t("assessment_card.ai.reset_assessment")}
                </SquareButton>
              ) : (
                <SquareButton
                  disabled={isDisabled || isRecordingAssessmentStyle}
                  eventName="Assessment - Assess with AI"
                  eventProps={{ pageId: page.id, submissionId: submission?.id }}
                  kind="tertiary"
                  size="xs"
                  onClick={() => onClickAiAssess(true)}
                >
                  {t("assessment_card.ai.assess_with_ai")}
                  <DoubleSparkleIcon />
                </SquareButton>
              )}
            </div>
          </form>
        </div>
      </ContentSidebarMain>

      {showOfficialFooter && (
        <OfficialSubmissionFooter
          isDisabled={isLoading || isLoadingOutcomeAssessments}
          isOfficial={isOfficial}
          onSetAsOfficial={onClickSetAsOfficial}
        />
      )}
    </div>
  );
};
